import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../../lib/supabase';
import { useOnboardingStore } from '../../store/onboarding.store';
import { Loader2, Building2, User, LogIn, ArrowRight, Mail } from 'lucide-react';

interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

export default function CreateGym() {
  const navigate = useNavigate();
  const { resetOnboarding } = useOnboardingStore();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [showOptions, setShowOptions] = useState(false);

  useEffect(() => {
    const checkAuthAndSetup = async () => {
      try {
        const { data: { session }, error: authError } = await supabase.auth.getSession();

        if (authError) {
          console.error('Error getting session:', authError.message);
          // No session, show options to sign in or sign up
          setShowOptions(true);
          setLoading(false);
          return;
        }

        if (session?.user) {
          // User is authenticated, get profile info
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('id, first_name, last_name')
            .eq('id', session.user.id)
            .single();

          setUser({
            id: session.user.id,
            first_name: profile?.first_name || null,
            last_name: profile?.last_name || null,
            email: session.user.email || '',
          });
          setShowOptions(true);
        } else {
          // No session, show options
          setShowOptions(true);
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        setShowOptions(true);
      } finally {
        setLoading(false);
      }
    };

    checkAuthAndSetup();
  }, []);

  const handleCreateWithCurrentAccount = () => {
    resetOnboarding();
    navigate('/onboarding');
  };

  const handleSignInDifferentAccount = async () => {
    // Sign out current user first
    await supabase.auth.signOut();
    // Navigate to sign in, then to create gym
    navigate('/signin?redirect=/create-gym');
  };

  const handleCreateNewAccount = () => {
    navigate('/owner-signup');
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Create Gym - RollOps</title>
          <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
          <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
          <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        </Helmet>
        <div className="min-h-screen bg-[#111010] flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 font-['Inter']">
        <div className="flex items-center space-x-4 mb-8">
          <img
            src="/rollops-logo.png"
            alt="RollOps Logo"
            className="w-16 h-16 sm:w-20 sm:h-20"
          />
          <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#EBFFEE]">
            Roll<span className="text-rollops-accent">O</span>ps
          </span>
        </div>
        <div className="flex flex-col items-center gap-4 text-[#EBFFEE]">
          <Loader2 size={48} className="animate-spin text-rollops-accent" />
          <p className="text-lg">Checking your account...</p>
        </div>
      </div>
      </>
    );
  }

  if (showOptions) {
    const displayName = user
      ? [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email
      : null;

    return (
      <>
        <Helmet>
          <title>Create Gym - RollOps</title>
          <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
          <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
          <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        </Helmet>
        <div className="min-h-screen bg-[#111010] flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 font-['Inter']">
        {/* Logo Header */}
        <div className="flex items-center space-x-4 mb-8">
          <img
            src="/rollops-logo.png"
            alt="RollOps Logo"
            className="w-16 h-16 sm:w-20 sm:h-20"
          />
          <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#EBFFEE]">
            Roll<span className="text-rollops-accent">O</span>ps
          </span>
        </div>

        {/* Main Card */}
        <div className="w-full max-w-md bg-[#221F1F] rounded-xl shadow-2xl p-8 border border-[#504A4A]">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
              <Building2 size={32} className="text-rollops-accent" />
            </div>
            <h1 className="text-2xl font-bold text-[#EBFFEE] mb-2">Create a New Gym</h1>
            <p className="text-[#A9A3A3]">
              Set up a new gym on RollOps
            </p>
          </div>

          <div className="space-y-4">
            {/* Option 1: Create with current account (if logged in) */}
            {user && (
              <button
                onClick={handleCreateWithCurrentAccount}
                className="w-full flex items-center gap-4 p-4 bg-[#363232] hover:bg-[#504A4A] rounded-lg transition-colors text-left group"
              >
                <div className="w-12 h-12 rounded-full bg-[#9B1421] flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#EBFFEE] truncate">
                    Continue as {displayName}
                  </p>
                  <p className="text-xs text-[#A9A3A3] truncate">
                    {user.email}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#A9A3A3] group-hover:text-[#EBFFEE] transition-colors" />
              </button>
            )}

            {/* Option 2: Sign in with different account */}
            {user && (
              <button
                onClick={handleSignInDifferentAccount}
                className="w-full flex items-center gap-4 p-4 bg-[#363232] hover:bg-[#504A4A] rounded-lg transition-colors text-left group"
              >
                <div className="w-12 h-12 rounded-full bg-[#504A4A] flex items-center justify-center flex-shrink-0">
                  <LogIn className="w-6 h-6 text-[#A9A3A3]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#EBFFEE]">
                    Use a different account
                  </p>
                  <p className="text-xs text-[#A9A3A3]">
                    Sign in with another RollOps account
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#A9A3A3] group-hover:text-[#EBFFEE] transition-colors" />
              </button>
            )}

            {/* Option 3: Create new account (if not logged in) OR additional option */}
            {!user ? (
              <>
                {/* Sign in option */}
                <Link
                  to="/signin?redirect=/create-gym"
                  className="w-full flex items-center gap-4 p-4 bg-[#363232] hover:bg-[#504A4A] rounded-lg transition-colors text-left group"
                >
                  <div className="w-12 h-12 rounded-full bg-[#9B1421] flex items-center justify-center flex-shrink-0">
                    <LogIn className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#EBFFEE]">
                      Sign in to your account
                    </p>
                    <p className="text-xs text-[#A9A3A3]">
                      Already have a RollOps account
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#A9A3A3] group-hover:text-[#EBFFEE] transition-colors" />
                </Link>

                {/* Create new account */}
                <button
                  onClick={handleCreateNewAccount}
                  className="w-full flex items-center gap-4 p-4 bg-[#363232] hover:bg-[#504A4A] rounded-lg transition-colors text-left group"
                >
                  <div className="w-12 h-12 rounded-full bg-[#504A4A] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#A9A3A3]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#EBFFEE]">
                      Create a new account
                    </p>
                    <p className="text-xs text-[#A9A3A3]">
                      New to RollOps? Sign up here
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#A9A3A3] group-hover:text-[#EBFFEE] transition-colors" />
                </button>
              </>
            ) : (
              <div className="pt-2 border-t border-[#504A4A]">
                <button
                  onClick={handleCreateNewAccount}
                  className="w-full flex items-center gap-4 p-4 bg-[#363232] hover:bg-[#504A4A] rounded-lg transition-colors text-left group"
                >
                  <div className="w-12 h-12 rounded-full bg-[#504A4A] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#A9A3A3]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#EBFFEE]">
                      Create a brand new account
                    </p>
                    <p className="text-xs text-[#A9A3A3]">
                      Sign up with a new email address
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#A9A3A3] group-hover:text-[#EBFFEE] transition-colors" />
                </button>
              </div>
            )}
          </div>

          {/* Back link */}
          <div className="mt-8 text-center">
            <Link
              to="/"
              className="text-sm text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
      </>
    );
  }

  return null;
}
